import React, { useState } from 'react';
import { getStrategyConfig } from '../config/strategyConfig';

/**
 * ActionPlan Component
 * Displays strategy-specific action steps with expand/collapse
 */
function ActionPlan({ strategyType, actionPlan = [], leverageOpportunities = [], riskMitigation = [] }) {
  const [expanded, setExpanded] = useState(false);
  const config = getStrategyConfig(strategyType);

  const actions = Array.isArray(actionPlan) ? actionPlan : [];
  const lev = Array.isArray(leverageOpportunities) ? leverageOpportunities : [];
  const risk = Array.isArray(riskMitigation) ? riskMitigation : [];

  // #region agent log
  if (
    typeof fetch !== 'undefined' &&
    (!Array.isArray(actionPlan) || !Array.isArray(leverageOpportunities) || !Array.isArray(riskMitigation))
  ) {
    fetch('http://127.0.0.1:7242/ingest/0f50a1bb-06d4-4480-a68c-70f1e59a674f', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        location: 'ActionPlan.jsx:normalize',
        message: 'non_array_action_fields_coerced',
        data: {
          ap: Array.isArray(actionPlan),
          lo: Array.isArray(leverageOpportunities),
          rm: Array.isArray(riskMitigation),
        },
        timestamp: Date.now(),
        hypothesisId: 'H7_actionplan',
      }),
    }).catch(() => {})
  }
  // #endregion

  if (actions.length === 0 && lev.length === 0 && risk.length === 0) {
    return null;
  }

  // Show first 4 items when collapsed
  const displayPlan = expanded ? actions : actions.slice(0, 4);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div 
        className={`${config.bgColor} ${config.borderColor} border-b px-6 py-4 flex items-center justify-between cursor-pointer`}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <span className="text-xl">🎯</span>
          <h4 className={`font-bold ${config.textColor}`}>
            Action Plan for {config.displayName}
          </h4>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">
            {expanded ? 'Collapse' : `${actions.length} steps`}
          </span>
          <svg
            className={`w-5 h-5 text-gray-500 transform transition-transform ${expanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Main Action Steps */}
        {displayPlan.length > 0 && (
          <div>
            <ol className="space-y-3">
              {displayPlan.map((step, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <span className={`flex-shrink-0 w-6 h-6 rounded-full ${config.accentColor} text-white flex items-center justify-center text-sm font-semibold`}>
                    {idx + 1}
                  </span>
                  <span className="text-gray-700 text-sm pt-0.5">{step}</span>
                </li>
              ))}
            </ol>
            
            {actions.length > 4 && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="mt-3 text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                {expanded ? 'Show less' : `+${actions.length - 4} more steps`}
              </button>
            )}
          </div>
        )}

        {/* Leverage Opportunities */}
        {lev.length > 0 && (
          <div className="pt-4 border-t border-gray-200">
            <h5 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <span>💡</span>
              <span>Leverage Opportunities</span>
            </h5>
            <ul className="space-y-2">
              {lev.map((item, idx) => (
                <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                  <span className="text-green-500">→</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Risk Mitigation */}
        {risk.length > 0 && (
          <div className="pt-4 border-t border-gray-200">
            <h5 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <span>🛡️</span>
              <span>Risk Mitigation</span>
            </h5>
            <ul className="space-y-2">
              {risk.map((item, idx) => (
                <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                  <span className="text-blue-500">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

export default ActionPlan;
